
import {
  computed,
  defineComponent,
  onBeforeMount,
  onErrorCaptured,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import ChatWidget from "./components/ChatWidget.vue";
import ChatWidgetPreview from "./components/ChatWidgetPreview.vue";
import { useApp } from "@/composables/use-app";
import * as EventTypes from "@/shared/eventTypes";
import { ChatWidgetConfig, WindowDimensions } from "@/shared/eventPayloads";
import { canUseLocalstorage } from "@/util/localstorage";
import { useIntervalFn } from "@vueuse/shared";
import { useWidgetVersion } from "@/composables/widgetVersion";
import { useConversation } from "@/composables/conversation";
import { useClientApi } from "@/composables/clientApi";
import { ClientApi } from "@/types/inboundWebhookPayload";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "App",
  components: {
    ChatWidget,
    ChatWidgetPreview,
  },
  setup() {
    const error = ref(false);
    const { locale } = useI18n();
    const {
      setVisitorCountryCode,
      visitorCountryCode,
      widgetId,
      setWidgetId,
      setWindowDimensions,
      setPreviewMode,
      isPreviewMode,
    } = useApp();
    const { getLatestWidgetVersion, setWidgetVersion, isVersionNewer } =
      useWidgetVersion();
    const { conversation, isWidgetOpen, setIsReady } = useConversation();
    const useLocalstorage = canUseLocalstorage();
    const { setClientConfig, clientConfig } = useClientApi();

    const { resume: startUpdateCheck } = useIntervalFn(
      async () => {
        const latestVersion = await getLatestWidgetVersion();

        if (
          latestVersion &&
          isVersionNewer(latestVersion) &&
          !isWidgetOpen.value
        ) {
          console.log(
            `New widget version ${latestVersion} is available, reloading ...`
          );
          window.parent.postMessage(
            {
              type: EventTypes.RELOAD_WIDGET,
              version: latestVersion,
            },
            "*"
          );
        }
      },
      60_000,
      {
        immediate: false,
      }
    );

    onBeforeMount(async () => {
      try {
        if (!visitorCountryCode.value) {
          const res = await fetch("https://d1m3kphglvh8g8.cloudfront.net/");
          const country = res.headers.get("Viewer-Country");

          if (country) {
            setVisitorCountryCode(country);
          }
        }
      } catch (err) {
        console.warn("Failed to get visitor country", err);
      }
    });

    onMounted(async () => {
      const latestVersion = await getLatestWidgetVersion();

      if (latestVersion) {
        setWidgetVersion(latestVersion);
        startUpdateCheck();
      }
    });

    const canLoad = computed(() => {
      if (!useLocalstorage) {
        console.error(
          "Browser does not allow the use of localstorage (incognito / private mode?). Chat widget cannot be loaded."
        );
        return false;
      }

      if (!widgetId.value) {
        return false;
      }

      return true;
    });

    onErrorCaptured((e) => {
      console.log("got error", e);
      error.value = true;
    });

    const receiveMessage = (msg: MessageEvent) => {
      if (msg.data?.type === EventTypes.SET_DEBUG_MODE) {
        console.log("TODO set debug mode");
        return;
      }

      if (msg.data?.type === EventTypes.CONFIG_UPDATED) {
        const config = msg.data.value as ClientApi;

        if (config) {
          setClientConfig(config);
        }

        return;
      }

      if (msg.data?.type === EventTypes.RESIZE) {
        const dim = msg.data.value as WindowDimensions;

        if (dim) {
          setWindowDimensions(dim);
        }

        return;
      }

      if (msg.data?.type === EventTypes.INIT_IFRAME) {
        const config = msg.data.value as ChatWidgetConfig;

        if (!config) {
          console.warn(
            "Got invalid widget config, cannot initialize chat widget"
          );
          return;
        }

        if (config.widgetId) {
          setWidgetId(config.widgetId);
          //initializeStorage(config.clientId)
        }

        if (config.dimensions) {
          setWindowDimensions(config.dimensions);
        }
      }

      if (msg.data?.type === EventTypes.INIT_IFRAME_PREVIEW) {
        setPreviewMode();

        const config = msg.data.value as ChatWidgetConfig;

        if (!config) {
          console.warn(
            "Got invalid widget config, cannot initialize chat widget"
          );
          return;
        }

        if (config.dimensions) {
          setWindowDimensions(config.dimensions);
        }

        setIsReady();
      }
    };

    onBeforeMount(() => {
      window.addEventListener("message", receiveMessage, {
        passive: true,
      });
    });

    onUnmounted(() => {
      window.removeEventListener("message", receiveMessage);
    });

    return { error, canLoad, isPreviewMode };
  },
});
